import { useState } from "react";
import {
  Box,
  Grid,
  Paper,
  useTheme,
  Tooltip,
  useMediaQuery,
  Chip,
} from "@mui/material";
import { Typography } from "@mui/material";
import { Button } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelIcon from "@mui/icons-material/Cancel";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { ISubscriptionPlan } from "../../../redux/subscription/subscription.types";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Delete } from "@mui/icons-material";

interface SubscriptionCardProps {
  plan: ISubscriptionPlan;
  isEdit?: boolean;
  handleEdit?: (plan: ISubscriptionPlan) => void;
  handleCopy?: (plan: ISubscriptionPlan) => void;
  onDelete?: (plan: ISubscriptionPlan) => void;
  handleChoosePlan?: (plan: ISubscriptionPlan) => Promise<void>;
  cardButtonText?: string;
  isCurrentPlan?: boolean;
  selectedCurrency?: string;
  hasDeleted?: boolean;
  isPaymentPending?: boolean;
}

const SubscriptionCard = ({
  plan,
  handleChoosePlan,
  isEdit = false,
  handleEdit,
  handleCopy,
  cardButtonText,
  isCurrentPlan = false,
  selectedCurrency = "INR",
  hasDeleted = false,
  isPaymentPending = false,
  onDelete,
}: SubscriptionCardProps) => {
  const theme = useTheme();
  const [viewMore, setViewMore] = useState(false);
  const [viewPlan] = useState<ISubscriptionPlan | null>(null);
  const [isHovered, setIsHovered] = useState(false);

  // Media queries for responsive behavior
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const isPopular = plan.set_default === 1;
  const isCurrent = isCurrentPlan;

  // Get price for selected currency
  const getPriceForCurrency = () => {
    if (!plan.price) return null;

    // If price is an array (new format)
    if (Array.isArray(plan.price)) {
      const priceEntry = plan.price.find(
        (p: any) => p.currency === selectedCurrency
      );
      return priceEntry ? priceEntry.amount : null;
    }

    // If price is a string/number (old format), show it with INR symbol
    return typeof plan.price === "string" || typeof plan.price === "number"
      ? plan.price
      : null;
  };

  const getCurrencySymbol = (currency: string) => {
    switch (currency) {
      case "USD":
        return "$";
      case "EUR":
        return "€";
      case "INR":
        return "₹";
      case "AED":
        return "د.إ";
      default:
        return "$";
    }
  };

  const currentPrice = getPriceForCurrency();
  const currencySymbol = getCurrencySymbol(selectedCurrency);

  return (
    <Box
      sx={{
        position: "relative",
        height: "100%",
        // width: {
        //   xs: "18rem",
        //   sm: "18rem",
        //   md: "22rem",
        // },
        // maxWidth: { xs: "100%", sm: "20rem", md: "22rem" },
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Mobile Action Strip - Above the card */}
      {isEdit && isMobile && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 1,
            mb: 1,
            px: 1,
          }}
        >
          <Tooltip title="Edit Plan" arrow>
            <Button
              variant="contained"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit && handleEdit(plan);
              }}
              sx={{
                minWidth: 0,
                width: 36,
                height: 36,
                borderRadius: "50%",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                "&:hover": {
                  boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                  transform: "translateY(-1px)",
                },
                transition: "all 0.2s ease",
              }}
            >
              <EditIcon sx={{ fontSize: "1rem" }} />
            </Button>
          </Tooltip>

          <Tooltip title="Copy Plan" arrow>
            <Button
              variant="contained"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                handleCopy && handleCopy(plan);
              }}
              sx={{
                minWidth: 0,
                width: 36,
                height: 36,
                borderRadius: "50%",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                "&:hover": {
                  boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                  transform: "translateY(-1px)",
                },
                transition: "all 0.2s ease",
              }}
            >
              <ContentCopyIcon sx={{ fontSize: "1rem" }} />
            </Button>
          </Tooltip>

          {onDelete && !hasDeleted && (
            <Tooltip title="Delete Plan" arrow>
              <Button
                variant="contained"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete && onDelete(plan);
                }}
                sx={{
                  minWidth: 0,
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  "&:hover": {
                    boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                    transform: "translateY(-1px)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                <Delete sx={{ fontSize: "1rem" }} />
              </Button>
            </Tooltip>
          )}
        </Box>
      )}

      <Paper
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => !isMobile && setIsHovered(false)}
        sx={{
          // opacity: hasDeleted ? 0.9 : 1,
          borderRadius: { xs: 2, sm: 3, md: 4 },
          overflow: "hidden",
          height: "100%",
          width: "100%",
          position: "relative",
          flex: 1,
          border: isCurrent
            ? `2px solid #4caf50` // Green border for current plan
            : isPopular
              ? `2px solid #1682D4`
              : "1px solid #e0e0e0",
          display: "flex",
          flexDirection: "column",
          boxShadow: isCurrent
            ? "0 8px 32px rgba(76, 175, 80, 0.2)" // Green shadow for current plan
            : isPopular
              ? "0 8px 32px rgba(0,0,0,0.12)"
              : "0 2px 8px rgba(0,0,0,0.04)",
          transform: isCurrent || isPopular ? "scale(1.05)" : "scale(1)",
          transition: "all 0.3s ease",
          backgroundColor: isCurrent ? "rgba(76, 175, 80, 0.02)" : "white", // Subtle green background
          // Only apply hover effects on desktop
          "&:hover": {
            ...(isMobile
              ? {}
              : {
                boxShadow: isCurrent
                  ? "0 8px 32px rgba(76, 175, 80, 0.3)"
                  : isPopular
                    ? "0 8px 32px rgba(0,0,0,0.2)"
                    : "0 4px 16px rgba(0,0,0,0.1)",
              }),
          },
        }}
      >
        {/* Hover Action Strip - Only for edit mode */}
        {isEdit && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: isMobile ? "0px" : isHovered ? "50px" : "0px",
              backgroundColor: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(10px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              padding: "0 16px",
              zIndex: 5,
              transition: "all 0.3s ease",
              overflow: "hidden",
            }}
          >
            <Box sx={{ display: "flex", gap: 1 }}>
              <Tooltip title="Edit Plan" arrow>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit && handleEdit(plan);
                  }}
                  sx={{
                    minWidth: 0,
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    "&:hover": {
                      boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                      transform: "translateY(-1px)",
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  <EditIcon sx={{ fontSize: "1rem" }} />
                </Button>
              </Tooltip>

              <Tooltip title="Copy Plan" arrow>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopy && handleCopy(plan);
                  }}
                  sx={{
                    minWidth: 0,
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    "&:hover": {
                      boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                      transform: "translateY(-1px)",
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  <ContentCopyIcon sx={{ fontSize: "1rem" }} />
                </Button>
              </Tooltip>
              {onDelete && !hasDeleted && (
                <Tooltip title="Delete Plan" arrow>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete && onDelete(plan);
                    }}
                    sx={{
                      minWidth: 0,
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                      "&:hover": {
                        boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                        transform: "translateY(-1px)",
                      },
                      transition: "all 0.2s ease",
                    }}
                  >
                    <Delete sx={{ fontSize: "1rem" }} />
                  </Button>
                </Tooltip>
              )}
            </Box>
          </Box>
        )}
        {/* Most Popular Badge */}
        {isPopular && !isCurrent && (
          <Box
            sx={{
              position: "absolute",
              top: isEdit && !isMobile && isHovered ? "50px" : "0",
              right: 0,
              background: `linear-gradient(135deg, #1682D4, ${theme.palette.secondary.main})`,
              color: "white",
              px: { xs: 2, sm: 3 },
              py: { xs: 0.5, sm: 1 },
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
              fontWeight: "bold",
              clipPath: "polygon(0 0, 100% 0, 100% 100%, 20% 100%)",
              minWidth: { xs: "80px", sm: "120px" },
              textAlign: "center",
              zIndex: 1,
              transition: "top 0.3s ease",
            }}
          >
            Free
          </Box>
        )}

        {isPaymentPending && !isCurrent && (
          <Box
            sx={{
              position: "absolute",
              top: isEdit && !isMobile && isHovered ? "50px" : "0",
              right: 0,
              background: `linear-gradient(135deg, #1682D4, ${theme.palette.secondary.main})`,
              color: "white",
              px: { xs: 2, sm: 3 },
              py: { xs: 0.5, sm: 1 },
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
              fontWeight: "bold",
              clipPath: "polygon(0 0, 100% 0, 100% 100%, 20% 100%)",
              minWidth: { xs: "80px", sm: "120px" },
              textAlign: "center",
              zIndex: 1,
              transition: "top 0.3s ease",
            }}
          >
            Payment in Progress
          </Box>
        )}

        {/* Selected Plan Badge */}
        {isCurrent && (
          <Box
            sx={{
              position: "absolute",
              top: isEdit && !isMobile && isHovered ? "50px" : "0",
              right: 0,
              background: `linear-gradient(135deg, #4caf50, #66bb6a)`,
              color: "white",
              px: { xs: 2, sm: 3 },
              py: { xs: 0.5, sm: 1 },
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
              fontWeight: "bold",
              clipPath: "polygon(0 0, 100% 0, 100% 100%, 20% 100%)",
              minWidth: { xs: "80px", sm: "120px" },
              textAlign: "center",
              zIndex: 1,
              transition: "top 0.3s ease",
            }}
          >
            Selected Plan
          </Box>
        )}

        <Box
          sx={{
            p: { xs: 3, sm: 4 },
            pt: isEdit
              ? {
                xs: 3, // Mobile doesn't show action strip
                sm: isHovered ? 7 : 4, // Desktop shows action strip on hover
              }
              : { xs: 3, sm: 4 },
            flex: 1,
            display: "flex",
            flexDirection: "column",
            transition: "padding-top 0.3s ease",
          }}
        >
          {/* Plan Name */}
          <Typography
            variant="h4"
            sx={{
              fontWeight: "bold",
              fontSize: {
                xs: "1.25rem",
                sm: "1.5rem",
                md: "1.75rem",
              },
              textTransform: "capitalize",
              mb: { xs: 0.5, sm: 1 },
              color: "#1a1a1a",
              lineHeight: 1.2,
              display: "inline",
              width: "fit-content",
            }}
          >
            {plan.plan_name}
            {hasDeleted && (
              <Box sx={{ display: "inline", width: "fit-content", ml: 2 }}>
                <Chip
                  size="medium"
                  label="DELETED"
                  color="error"
                  sx={{ fontWeight: 600, fontSize: "0.9rem" }}
                />
              </Box>
            )}
          </Typography>

          {/* Price */}
          <Box sx={{ mb: { xs: 2, sm: 3 } }}>
            {currentPrice !== null ? (
              <Box sx={{ display: "flex", alignItems: "baseline", mb: 1 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: { xs: "1rem", sm: "1.125rem" },
                    color: "#666",
                    mr: 0.5,
                  }}
                >
                  {currencySymbol}
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: "bold",
                    color: "#1a1a1a",
                    fontSize: {
                      xs: "1.75rem",
                      sm: "2.25rem",
                      md: "2.75rem",
                    },
                    lineHeight: 1,
                  }}
                >
                  {currentPrice?.toLocaleString()}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Typography
                  variant="body1"
                  sx={{
                    color: "#999",
                    fontStyle: "italic",
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                  }}
                >
                  Price not available in {selectedCurrency}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Features List */}
          <Box sx={{ flex: 1, mb: { xs: 2, sm: 3 } }}>
            {plan?.subscription_plan_features &&
              [...plan?.subscription_plan_features]
                .filter((feature) => feature.show_on_card === 1)
                .sort((a, b) => (a.priority || 0) - (b.priority || 0))
                .map((feature, idx: number) => (
                  <Box
                    key={idx}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: { xs: 1.5, sm: 2 },
                      justifyContent: "space-between",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flex: 1,
                        cursor: "pointer",
                      }}
                    >
                      {feature.is_included ? (
                        <CheckCircleOutlineIcon
                          sx={{
                            color: isPopular ? "#1682D4" : "#10b981",
                            mr: { xs: 1.5, sm: 2 },
                            fontSize: { xs: "1rem", sm: "1.25rem" },
                            flexShrink: 0,
                          }}
                        />
                      ) : (
                        <CancelIcon
                          sx={{
                            color: isPopular ? "#1682D4" : "error.main",
                            mr: { xs: 1.5, sm: 2 },
                            fontSize: { xs: "1rem", sm: "1.25rem" },
                            flexShrink: 0,
                          }}
                        />
                      )}

                      <Typography
                        variant="body1"
                        sx={{
                          fontSize: { xs: "0.875rem", sm: "1rem" },
                          color: "#374151",
                          fontWeight: 500,
                          lineHeight: 1.4,
                        }}
                      >
                        {feature?.feature_name?.split("_").join(" ")}
                      </Typography>
                    </Box>

                    <Tooltip
                      title={`${feature.description?.split("_").join(" ")}`}
                      arrow
                      placement="top"
                    >
                      <InfoOutlinedIcon
                        sx={{
                          color: "#9ca3af",
                          fontSize: { xs: "0.875rem", sm: "1rem" },
                          ml: 1,
                          cursor: "pointer",
                          "&:hover": {
                            color: theme.palette.primary.main,
                          },
                        }}
                      />
                    </Tooltip>
                  </Box>
                ))}
          </Box>

          {/* CTA Button */}
          <Button
            onClick={() =>
              !isCurrent && handleChoosePlan && handleChoosePlan(plan)
            }
            variant={
              isCurrent ? "contained" : isPopular ? "contained" : "outlined"
            }
            fullWidth
            disabled={isCurrent || hasDeleted || isPaymentPending}
            sx={{
              borderRadius: { xs: 2, sm: 3 },
              textTransform: "none",
              py: { xs: 1, sm: 0.8 },
              px: 2,
              fontSize: { xs: "1rem", sm: "1.125rem" },
              fontWeight: "bold",
              border: isCurrent
                ? "none"
                : isPopular
                  ? "none"
                  : `2px solid ${theme.palette.primary.main}`,
              ...(isCurrent
                ? {
                  background: `linear-gradient(135deg, #4caf50, #66bb6a)`,
                  color: "white",
                  cursor: "default",
                  "&:hover": {
                    background: `linear-gradient(135deg, #4caf50, #66bb6a)`,
                  },
                }
                : isPopular
                  ? {
                    background: `linear-gradient(135deg, #1682D4, ${theme.palette.secondary.main})`,
                    color: "white",
                    "&:hover": {
                      background: `linear-gradient(135deg, #1365B8, ${theme.palette.secondary.dark})`,
                      boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
                    },
                  }
                  : {
                    color: theme.palette.primary.main,
                    backgroundColor: "transparent",
                    "&:hover": {
                      backgroundColor: theme.palette.primary.main,
                      color: "white",
                      borderColor: theme.palette.primary.main,
                    },
                  }),
            }}
          >
            {cardButtonText}
            {!isPaymentPending && <Box
              component="span"
              sx={{
                ml: 1,
                fontSize: { xs: "1.125rem", sm: "1.25rem" },
                fontWeight: "normal",
              }}
            >
              →
            </Box>}
          </Button>
        </Box>
      </Paper>

      {/* View More Modal */}
      {viewMore && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
            p: 2,
          }}
          onClick={() => setViewMore(false)}
        >
          <Paper
            sx={{
              maxWidth: { xs: "95%", sm: "600px" },
              width: "100%",
              maxHeight: "85vh",
              overflow: "auto",
              p: { xs: 2, sm: 3 },
              borderRadius: { xs: 1, sm: 2 },
              position: "relative",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: "bold",
                  color: theme.palette.primary.main,
                  fontSize: { xs: "1.25rem", sm: "1.5rem" },
                }}
              >
                Plan Features
              </Typography>
              <Button
                onClick={() => setViewMore(false)}
                sx={{
                  minWidth: "auto",
                  p: 1,
                  px: 2,
                  color: theme.palette.text.secondary,
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.1)",
                  },
                }}
              >
                ✕
              </Button>
            </Box>
            <Box>
              {viewPlan && (
                <Box key={viewPlan.subscription_plan_uuid} sx={{ mb: 4 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 2,
                      fontWeight: "bold",
                      color: theme.palette.primary.main,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      pb: 1,
                      fontSize: { xs: "1rem", sm: "1.25rem" },
                    }}
                  >
                    {viewPlan.plan_name} -{" "}
                    {currentPrice !== null
                      ? `${currencySymbol}${currentPrice?.toLocaleString()}`
                      : "Price not available"}
                    /{viewPlan.billing_cycle}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      mb: 2,
                      color: theme.palette.text.secondary,
                      fontStyle: "italic",
                      fontSize: { xs: "0.875rem", sm: "0.875rem" },
                    }}
                  >
                    {viewPlan.description}
                  </Typography>

                  <Grid container spacing={1}>
                    {viewPlan.subscription_plan_features &&
                      viewPlan.subscription_plan_features
                        .filter((feature) => feature.show_on_card === 1)
                        .map((feature, idx: number) => (
                          <Box
                            sx={{
                              width: "100%",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                            key={idx}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                mb: 1,
                                p: 1,
                              }}
                            >
                              <CheckCircleOutlineIcon
                                sx={{
                                  color: "#00c853",
                                  mr: 1,
                                  fontSize: "1.1rem",
                                  mt: 0.1,
                                  flexShrink: 0,
                                }}
                              />
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: { xs: "0.8rem", sm: "0.9rem" },
                                  lineHeight: 1.4,
                                }}
                              >
                                {feature.description}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                  </Grid>
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default SubscriptionCard;
